import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types
export interface SparcWalkAPIData {
  ID: number;
  Title: string;
  Walk_Type: string;
  Created: string;
  Created_By: string;
  status: string;
}

export interface SparcWalkRowData {
  id: number;
  details: string;
  date: string;
  by: string;
  status: string;
  [key: string]: any;
}

export interface SparcWalkFilters {
  zone: string;
  category: string;
  // page: number;
  // pageSize: number;
}

export interface QuestionItem {
  id: string;
  Question: string;
  ID?: number;
  [key: string]: any;
}

interface SparcWalkState {
  sparcWalks: SparcWalkAPIData[];
  sparcWalkRows: SparcWalkRowData[];
  selectedFilters: SparcWalkFilters;
  walkType: string;
  hiddenWalk: string;
  selectedWalk: SparcWalkRowData | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  behaviourData: QuestionItem[];
  questionData: QuestionItem[];
  zone?: string;
  centralizedErrors: CentralizedError[];
  isErrorModalVisible: boolean;
}

// Centralized Error Interface
export interface CentralizedError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: {
    originalMessage?: string;
    location?: string;
    zone?: string;
    category?: string;
    walkId?: number | string;
    [key: string]: any;
  };
}

const initialState: SparcWalkState = {
  sparcWalks: [],
  sparcWalkRows: [],
  questionData: [],
  selectedFilters: {
    zone: "All",
    category: "All",
    // page: 1,
    // pageSize: 10,
  },
  walkType: "SPARC Walk",
  hiddenWalk: "SPARC Walk",
  selectedWalk: null,
  loading: false,
  error: null,
  successMessage: null,
  behaviourData: [],
  zone: "",
  centralizedErrors: [],
  isErrorModalVisible: false,
};

const sparcWalkSlice = createSlice({
  name: "sparcWalk",
  initialState,
  reducers: {
    setSparcWalks: (state, action: PayloadAction<SparcWalkAPIData[]>) => {
      state.sparcWalks = action.payload;
    },
    setZone: (state, action) => {
      state.zone = action.payload;
    },
    setSparcWalkRows: (state, action: PayloadAction<SparcWalkRowData[]>) => {
      state.sparcWalkRows = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<Partial<SparcWalkFilters>>
    ) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },
    resetFilters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
    },
    setBehaviourData: (state, action: PayloadAction<QuestionItem[]>) => {
      state.behaviourData = action.payload;
    },
    setQuestionData: (state, action: PayloadAction<QuestionItem[]>) => {
      state.questionData = action.payload;
    },
    setWalkConfig: (
      state,
      action: PayloadAction<{ walkType?: string; hiddenWalk?: string }>
    ) => {
      if (action.payload.walkType !== undefined) {
        state.walkType = action.payload.walkType;
      }
      if (action.payload.hiddenWalk !== undefined) {
        state.hiddenWalk = action.payload.hiddenWalk;
      }
    },
    setSelectedWalk: (
      state,
      action: PayloadAction<SparcWalkRowData | null>
    ) => {
      state.selectedWalk = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Centralized Error Handling Actions
    addCentralizedError(state, action: PayloadAction<CentralizedError>) {
      state.centralizedErrors.push(action.payload);
      // Keep only the last 10 errors to prevent memory issues
      if (state.centralizedErrors.length > 10) {
        state.centralizedErrors = state.centralizedErrors.slice(-10);
      }
    },
    setErrorModalVisible(state, action: PayloadAction<boolean>) {
      state.isErrorModalVisible = action.payload;
    },
    clearCentralizedErrors(state) {
      state.centralizedErrors = [];
      state.isErrorModalVisible = false;
    },
  },
});

export const {
  setSparcWalks,
  setSparcWalkRows,
  setSelectedFilters,
  resetFilters,
  setWalkConfig,
  setSelectedWalk,
  setLoading,
  setError,
  clearError,
  setSuccessMessage,
  clearSuccessMessage,
  setBehaviourData,
  setQuestionData,
  setZone,
  addCentralizedError,
  setErrorModalVisible,
  clearCentralizedErrors,
} = sparcWalkSlice.actions;

// Selectors
export const selectCentralizedErrors = (state: any) => state.sparcWalk.centralizedErrors;
export const selectIsErrorModalVisible = (state: any) => state.sparcWalk.isErrorModalVisible;

// Selector to get period/week from appData
export const selectPeriodWeek = (state: any) => state.appData.periodWeek?.data;

// Use selectPeriodWeek(state) in your submit logic to get { period, week }

export default sparcWalkSlice.reducer;
